export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
  /**
   * @deprecated Un solo tamaño en toda la app. La prop queda para no romper a
   *   quien la pase, pero no cambia nada: el cotejo mide --evo-checkbox-sz
   *   siempre. Antes 'md' daba 18px y bastaba con que una lista lo usara para
   *   que sus cotejos no alinearan con los de al lado.
   */
  size?: 'sm' | 'md';
  disabled?: boolean;
}

function CheckIcon({ px }: { px: number }) {
  return (
    <svg width={px * 0.55} height={px * 0.55} viewBox="0 0 10 8" fill="none">
      <path d="M1 4L3.5 6.5L9 1" stroke="var(--evo-bg)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IndeterminateIcon({ px }: { px: number }) {
  return (
    <svg width={px * 0.5} height={px * 0.5} viewBox="0 0 10 2" fill="none">
      <path d="M1 1H9" stroke="var(--evo-bg)" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function Checkbox({ checked, onChange, indeterminate, size = 'sm', disabled }: CheckboxProps) {
  // V2: el cotejo canónico mide --evo-checkbox-sz (14px); border-box para
  // que marcado y sin marcar midan IGUAL (error histórico del handoff).
  //
  // Un solo tamaño (2026-09-03, pedido de Eduardo): todos los cotejos de EvoHub
  // miden lo mismo. `size` ya no decide nada — dos tamaños posibles es una
  // desalineación esperando a que alguien elija el otro.
  void size;
  const px = 14;
  const active = checked || (indeterminate ?? false);

  // Bug real, tercera vez que aparece (2026-08-02): páginas que envuelven este
  // checkbox en una fila clickeable (para que todo el ancho de la fila
  // seleccione, no solo el cuadradito) reciben el click DOS veces — una acá
  // adentro, otra por bubbling hacia el onClick de la fila — si esa fila hace
  // un toggle (no un set absoluto), las dos llamadas se cancelan entre sí y
  // el click en el checkbox no hace nada. El parche de cada página
  // (pointerEvents:'none' + wrapper) tapaba el síntoma ahí, pero cualquier
  // fila nueva que no lo copie vuelve a romperse — closest, arreglarlo acá
  // adentro para que ningún caller tenga que acordarse de nada.
  function handleClick(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    if (disabled) return;
    if (indeterminate) {
      onChange(true);
    } else {
      onChange(!checked);
    }
  }

  return (
    <div
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={e => { if ((e.key === ' ' || e.key === 'Enter') && !disabled) { e.preventDefault(); handleClick(e); } }}
      style={{
        width: 'var(--evo-checkbox-sz)',
        height: 'var(--evo-checkbox-sz)',
        boxSizing: 'border-box',
        flexShrink: 0,
        borderRadius: 'var(--evo-checkbox-r)',
        border: `var(--evo-checkbox-bw) solid ${active ? 'var(--evo-cyan)' : 'var(--evo-fg-3)'}`,
        background: active ? 'var(--evo-cyan)' : 'var(--evo-bg-3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'border-color 100ms, background 100ms',
        opacity: disabled ? 0.4 : 1,
        outline: 'none',
        userSelect: 'none',
      }}
      onMouseEnter={e => {
        if (!disabled && !active) {
          (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--evo-cyan)';
        }
      }}
      onMouseLeave={e => {
        if (!disabled && !active) {
          (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--evo-fg-3)';
        }
      }}
    >
      {indeterminate ? <IndeterminateIcon px={px} /> : checked ? <CheckIcon px={px} /> : null}
    </div>
  );
}
